import * as React from 'react'
import { Outlet, useOutletContext } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import { EmployeesListPageDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'
import { orderBy } from 'modules/core/lib/filters'
import type FiltersForm from 'modules/core/lib/forms/Filters'
import { isFeatureAvailabilityEnabled } from 'modules/core/lib/isFeatureAvailabilityEnabled'

import contractVersions from 'modules/contracts/store/collections/contractVersions'
import ImporterBanner from 'modules/employees/components/ImporterBanner'
import type { RouteContextParams } from 'modules/employees/lib/RouteContextParams'
import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { useGetMarketplaceCompanyInstallations } from 'modules/marketplace/hooks/integrations/companyInstallations/useGetMarketplaceCompanyInstallations'
import { DATEV_LAUDS_INTEGRATION_ID } from 'modules/marketplace/lib/integrationIds'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import ImporterCallout from './components/ImporterCallout'
import Content from './components/_Content'
import Header from './components/_Content/_Header'
import EmployeesInsights from './components/_EmployeesInsights'
import ListEmployees from './components/_ListEmployees'
import TrialNoticeBox from './components/_TrialEmployees/_TrialNoticeBox'
import { useEmployeeHeaderPermissions } from './hooks/useEmployeeHeaderPermissions'

interface Props {
  filtersForm: FiltersForm
}

const useSortedEmployees = (employees: Employee[], form: FiltersForm) => {
  const { loading, value: sortedEmployees } = useAsyncData(
    () => orderBy(form, employees),
    [
      form.get('filterBy').value,
      form.get('sortDirection').value,
      form.get('sortAttribute').value,
      employees.length,
    ]
  )

  return {
    loading,
    sortedEmployees: sortedEmployees ?? [],
  }
}

const onDeleteTrialEmployeesSuccess = async () => {
  // NOTE: is there a better way to do this? want to refresh the list of employees
  // but employees.fetch() doesn't work as i expected.
  window.location.reload()
}

const EmployeesListPage = ({ filtersForm }: Props) => {
  const { company } = useCurrentSession()
  const { data, loading: loadingQuery } = useQuery(EmployeesListPageDocument, {
    variables: {
      companyId: company.id,
      featureAvailabilityIds: [Features.compensations_management],
    },
  })
  const { loading: loadingSorting, sortedEmployees } = useSortedEmployees(
    employees.toArray(),
    filtersForm
  )
  const { data: companyInstallationsData } = useGetMarketplaceCompanyInstallations(
    {
      companyIds: [company.id],
    },
    false
  )

  const canSeeLegalEntityEmployees =
    data?.apiCore._policies.companySeeLegalEntityEmployees.can ?? false
  const canEditContractsSettings =
    data?.contracts._policies.contractEditContractsSettings.can ?? false
  const loading = loadingQuery || loadingSorting
  const canEditInBulk = data?.employees._policies.editEmployeesInBulkButton.can ?? false

  const canEditContractsInBulk = data?.contracts._policies.contractEditContractVersion?.can ?? false

  const header = useEmployeeHeaderPermissions({
    canImportEmployees: data?.onboardingImporter._policies.importEmployees.can ?? false,
    loadingData: loading,
    hasDatevIntegration: companyInstallationsData?.some(
      (installation) => installation.integrationDatoId === DATEV_LAUDS_INTEGRATION_ID
    ),
  })
  const employeeListContext = useOutletContext<RouteContextParams>()
  const canDeleteTrialEmployeesInBulk = data?.trial._policies.trialEmployeeBulkDelete.can
  const featureFlags = data?.features.featuresConnection.nodes ?? []

  const legacyEmployeeImportFeatureFlag = featureFlags.find(
    (featureFlag) => featureFlag.name === 'legacy_employee_import'
  )

  const legacyEmployeeEditionFeatureFlag = featureFlags.find(
    (featureFlag) => featureFlag.name === 'legacy_employee_edition'
  )

  const universalImporterContractsEditionFeatureFlag = featureFlags.find(
    (featureFlag) => featureFlag.name === 'dev_universal_contracts_edition'
  )

  const universalImporterContractsImportFeatureFlag = featureFlags.find(
    (featureFlag) => featureFlag.name === 'dev_universal_contracts_import'
  )

  const devEmployeesPageInsightsFeatureFlag = featureFlags.find(
    (featureFlag) => featureFlag.name === 'dev_employees_page_insights'
  )
  const featureAvailabilities = data?.companies?.featureAvailabilitiesConnection?.nodes || []
  const hasCompensationsModule = isFeatureAvailabilityEnabled(
    featureAvailabilities,
    Features.compensations_management
  )

  const canInviteEmployees = policyAuthorizations.for('employee.invite')
  const showInviteBox = sortedEmployees.length > 0 && canInviteEmployees

  const notTerminatedEmployees = sortedEmployees.filter((employee) => !employee.isTerminated)

  useAsyncData(async () => {
    return policyAuthorizations.authorizePolicies('employee.invite')
  }, [])

  useEffect(() => {
    track(trackingEvents.employee.page.employeeList.visited)
  }, [])

  const showImporterBanner =
    legacyEmployeeImportFeatureFlag?.enabled === false && notTerminatedEmployees.length === 1

  const [showLegalEntitySelectorModal, setShowLegalEntitySelectorModal] = React.useState(false)

  return (
    <ResponsivePage.Body
      showHeader={showInviteBox && notTerminatedEmployees.length > 1}
      header={{
        widget: <Header />,
      }}
    >
      <Box gap="s32">
        <ImporterCallout />
        <Content>
          <ErrorBoundary>
            {company.subscription.inFreeTrial && canDeleteTrialEmployeesInBulk && (
              <TrialNoticeBox onSuccess={onDeleteTrialEmployeesSuccess} />
            )}

            {showImporterBanner && (
              <ImporterBanner clickHandler={() => setShowLegalEntitySelectorModal(true)} />
            )}

            {!!devEmployeesPageInsightsFeatureFlag?.enabled && (
              <EmployeesInsights
                companyId={company.id}
                endDate={dateToString(moment(), 'backend')}
                startDate={dateToString(moment().subtract(11, 'months'), 'backend')}
              />
            )}
            <ListEmployees
              filtersForm={filtersForm}
              employees={sortedEmployees}
              loadingPageData={loading}
              canEditInBulk={canEditInBulk}
              canEditContractsInBulk={canEditContractsInBulk}
              showAction={header?.action.show ?? false}
              actionTo={header?.action.to || ''}
              showOptions={header?.options.show ?? false}
              options={header?.options.options || []}
              contractVersions={contractVersions}
              legacyEmployeeEdition={legacyEmployeeEditionFeatureFlag?.enabled ?? false}
              universalImporterContractsEditionFeatureFlag={
                universalImporterContractsEditionFeatureFlag?.enabled ?? false
              }
              universalImporterContractsImportFeatureFlag={
                universalImporterContractsImportFeatureFlag?.enabled ?? false
              }
              universalEmployeesImporterEnabled={legacyEmployeeImportFeatureFlag?.enabled === false}
              hasCompensationsModule={!!hasCompensationsModule}
              showLegalEntitySelectorModal={showLegalEntitySelectorModal}
              setLegalEntitySelectorModal={setShowLegalEntitySelectorModal}
              canSeeLegalEntityEmployees={canSeeLegalEntityEmployees}
              canEditContractsSettings={canEditContractsSettings}
            />
          </ErrorBoundary>
        </Content>
      </Box>
      <Outlet context={employeeListContext} />{' '}
    </ResponsivePage.Body>
  )
}

export default observer(EmployeesListPage)
